/**
 * AddSupplierModal Component
 * 
 * Modal for adding new suppliers to the system.
 * Integrates with the backend supplier creation endpoint.
 * 
 * @component
 * @example
 * <AddSupplierModal 
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   onSuccess={handleSupplierAdded}
 * />
 */

import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import CategorySelector from '../../../common/CategorySelector';
import CompanySelector from '../../../common/CompanySelector';
import './AddSupplierModal.scss';

interface AddSupplierModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Close modal callback */
  onClose: () => void;
  /** Success callback after supplier is added */
  onSuccess?: (data?: any) => void;
  /** Optional company ID for company-specific supplier addition */
  companyId?: string;
  /** Optional company name for company-specific supplier addition */
  companyName?: string;
  /** User object to determine company auto-selection and approval status */
  user?: any;
  /** If true, hides advanced fields (scores, tags, online_sales) for basic users */
  isBasicUser?: boolean;
  /** Current region selected in the dashboard */
  currentRegion?: string;
}

interface SupplierFormData {
  supplier_id?: string;
  name: string;
  emails: string[];
  phone: string;
  website: string;
  region: string;
  categories: string[];
  contact_person: string;
  notes: string;
  address: string;
  commercial_name: string;
  online_sales: boolean;
  trustpilot_score: number;
  google_maps_score: number;
  mebsis_score: number;
  tags: string;
  mebsis_registration: string;
  has_website: boolean;
  status?: string;
  company_specific_codes?: Array<{ company_id: string; company_name: string; code: string }>;
}

const AddSupplierModal: React.FC<AddSupplierModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  companyId,
  companyName,
  user,
  isBasicUser = false,
  currentRegion
}) => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState<'single' | 'excel'>('single');

  // Determine if user is top-level Gordian admin (no company_id)
  const isTopLevelAdmin = user?.role === 'admin' && !user?.company_id && !user?.companyId;

  const [formData, setFormData] = useState<SupplierFormData>({
    name: '',
    emails: [''], // Start with one empty email field
    phone: '',
    website: '',
    region: currentRegion || 'tr',
    categories: [],
    contact_person: '',
    notes: '',
    address: '',
    commercial_name: '',
    online_sales: false,
    trustpilot_score: 0,
    google_maps_score: 0,
    mebsis_score: 0,
    tags: '',
    mebsis_registration: 'Not Registered',
    has_website: true,
    status: 'active' // Default to active for all new suppliers
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Excel import states
  const [excelData, setExcelData] = useState<any[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [editedData, setEditedData] = useState<any>({});
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Website suggestion states
  const [websiteSuggestions, setWebsiteSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionTimeout, setSuggestionTimeout] = useState<NodeJS.Timeout | null>(null);

  // Company codes states
  const [selectedCompanies, setSelectedCompanies] = useState<Array<{ company_id: string; company_name: string }>>([]);
  const [companyCodes, setCompanyCodes] = useState<Record<string, string>>({});

  // Auto-select company if companyId is provided OR if user has a company
  useEffect(() => {
    if (!isOpen) return;

    if (companyId && companyName) {
      // Company explicitly provided (e.g., from CompanySuppliersDashboard)
      setSelectedCompanies([{ company_id: companyId, company_name: companyName }]);
      // Set default company code to '1'
      setCompanyCodes({ [companyId]: '1' });
    } else if (user?.company_id && user?.company_name) {
      // Auto-select user's company if they have one
      setSelectedCompanies([{ company_id: user.company_id, company_name: user.company_name }]);
      // Set default company code to '1'
      setCompanyCodes({ [user.company_id]: '1' });
    } else if (user?.companyId && user?.companyName) {
      // Alternative company field names
      setSelectedCompanies([{ company_id: user.companyId, company_name: user.companyName }]);
      setCompanyCodes({ [user.companyId]: '1' });
    }
  }, [companyId, companyName, user, isOpen]);

  // Helper functions for multiple selections
  const addEmail = () => {
    setFormData(prev => ({
      ...prev,
      emails: [...prev.emails, '']
    }));
  };

  const removeEmail = (index: number) => {
    setFormData(prev => ({
      ...prev,
      emails: prev.emails.filter((_, i) => i !== index)
    }));
  };

  const updateEmail = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      emails: prev.emails.map((email, i) => i === index ? value : email)
    }));
  };


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));

      // If "has_website" is unchecked, clear the website field
      if (name === 'has_website' && !checked) {
        setFormData(prev => ({
          ...prev,
          website: ''
        }));
      }
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Supplier name is required');
      return false;
    }

    // Check if at least one non-empty email exists
    const validEmails = formData.emails.filter(email => email.trim());
    if (validEmails.length === 0) {
      setError('At least one email is required');
      return false;
    }

    if (formData.categories.length === 0) {
      setError('At least one category is required');
      return false;
    }

    // Relaxed website validation - just check if it has a domain structure
    if (formData.has_website && formData.website.trim()) {
      const website = formData.website.trim();
      // Simple check: must contain at least a dot (e.g., "dk.com")
      if (!website.includes('.')) {
        setError('Website must be a valid domain (e.g., dk.com)');
        return false;
      }
    } else if (formData.has_website) {
      setError('Website is required when "Has Website" is checked');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Use token from useAuth hook
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Build company_specific_codes from selectedCompanies and companyCodes
      // Use default '1' if code is not provided
      const companySpecificCodes = selectedCompanies.map(company => ({
        company_id: company.company_id,
        company_name: company.company_name,
        code: companyCodes[company.company_id] || '1' // Default to '1' if not specified
      }));

      // Prepare data for submission
      const submitData = {
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        company_specific_codes: companySpecificCodes,
        status: 'active' // Valid statuses: active, inactive, pending, suspended
      };

      // Use company-specific endpoint if companyId is provided
      const endpoint = companyId
        ? `${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/suppliers`
        : API_ENDPOINTS.SUPPLIERS_V2;

      console.log('🎯 Using endpoint:', endpoint, 'companyId:', companyId);
      console.log('🔍 SUBMIT DEBUG: Company codes:', companySpecificCodes);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        let errorMessage = `Failed to create supplier (HTTP ${response.status})`;

        try {
          const errorData = await response.json();

          // Handle domain conflict specifically
          if (response.status === 409 && errorData.existing_supplier) {
            const existing = errorData.existing_supplier;
            throw new Error(
              `Supplier with domain "${existing.website}" already exists. ` +
              `Existing supplier: ${existing.name} (${existing.supplier_id})`
            );
          }

          // Extract detailed error message from backend
          errorMessage = errorData.error || errorData.message || errorMessage;

          // Provide more context for common 400 errors
          if (response.status === 400) {
            if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            } else {
              errorMessage = 'Invalid input data. Please check all required fields and try again.';
            }
          }

        } catch (parseError) {
          // If JSON parsing fails, use status text or default message
          errorMessage = response.statusText || `Failed to create supplier (HTTP ${response.status})`;
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.success) {
        // Reset form
        setFormData({
          name: '',
          emails: [''],
          phone: '',
          website: '',
          region: currentRegion || 'tr',
          categories: [],
          contact_person: '',
          notes: '',
          address: '',
          commercial_name: '',
          online_sales: false,
          trustpilot_score: 0,
          google_maps_score: 0,
          mebsis_score: 0,
          tags: '',
          mebsis_registration: 'Not Registered',
          has_website: true
        });

        // Reset company codes states (will be re-initialized if companyId exists)
        setSelectedCompanies([]);
        setCompanyCodes({});

        // Call success callback
        if (onSuccess) {
          onSuccess(result.data);
        }

        // Close modal
        onClose();
      } else {
        throw new Error(result.error || 'Failed to create supplier');
      }
    } catch (error) {
      console.error('Error creating supplier:', error);
      setError(error instanceof Error ? error.message : 'Failed to create supplier');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Clear timeout
    if (suggestionTimeout) {
      clearTimeout(suggestionTimeout);
    }

    setFormData({
      name: '',
      emails: [''],
      phone: '',
      website: '',
      region: currentRegion || 'tr',
      categories: [],
      contact_person: '',
      notes: '',
      address: '',
      commercial_name: '',
      online_sales: false,
      trustpilot_score: 0,
      google_maps_score: 0,
      mebsis_score: 0,
      tags: '',
      mebsis_registration: 'Not Registered',
      has_website: true
    });
    setError(null);
    setActiveTab('single');
    setExcelData([]);
    setShowPreview(false);
    setEditingRow(null);
    setEditedData({});
    setSelectedRows(new Set());
    setWebsiteSuggestions([]);
    setShowSuggestions(false);
    setSuggestionTimeout(null);
    onClose();
  };

  // Excel import functions
  const downloadTemplate = () => {
    const headers = [
      'name',
      'email',
      'phone',
      'website',
      'region',
      'category',
      'contact_person',
      'notes',
      'address',
      'commercial_name',
      'online_sales',
      'online_marketing',
      'trustpilot_score',
      'google_maps_score',
      'mebsis_score',
      'email_score',
      'tags',
      'mebsis_registration'
    ];

    // Dummy data examples
    const dummyData = [
      [
        'Acme Construction Materials',
        'info@acme-construction.com',
        '+90 212 555 0101',
        'acme-construction.com',
        'Turkey',
        'Construction Materials',
        'Mehmet Yılmaz',
        'Leading construction materials supplier in Istanbul',
        'Maslak Mahallesi, Büyükdere Cad. No:123, Sarıyer/İstanbul',
        'ACME Construction',
        'true',
        'true',
        '4.2',
        '4.5',
        '4.0',
        '4.1',
        'construction,materials,concrete',
        'Active'
      ],
      [
        'TechElectro Solutions',
        'sales@techelectro.com.tr',
        '+90 216 555 0202',
        'techelectro.com.tr',
        'Turkey',
        'Electronics',
        'Ayşe Demir',
        'Specialized in industrial electronics and automation',
        'Ataşehir Mahallesi, Barbaros Bulvarı No:456, Ataşehir/İstanbul',
        'TechElectro',
        'true',
        'false',
        '3.8',
        '4.0',
        '3.5',
        '3.9',
        'electronics,automation,industrial',
        'Active'
      ],
      [
        'GreenEnergy Systems',
        'contact@greenenergy.com',
        '+90 312 555 0303',
        'greenenergy-systems.com',
        'Turkey',
        'Industrial Services',
        'Can Özkan',
        'Renewable energy solutions and solar panel installations',
        'Çankaya Mahallesi, Tunalı Hilmi Cad. No:789, Çankaya/Ankara',
        'GreenEnergy',
        'false',
        'true',
        '4.5',
        '4.7',
        '4.3',
        '4.4',
        'renewable,energy,solar,green',
        'Pending'
      ]
    ];

    const csvContent = headers.join(',') + '\n' +
      dummyData.map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'suppliers_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleExcelUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim());

      const data = lines.slice(1)
        .filter(line => line.trim())
        .map((line, index) => {
          const values = line.split(',').map(v => v.trim());
          const row: any = {};
          headers.forEach((header, i) => {
            row[header] = values[i] || '';
          });
          return { ...row, _tempId: index };
        });

      setExcelData(data);
      setShowPreview(true);
    };
    reader.readAsText(file);
  };

  const handleBulkCreate = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Use token from useAuth hook
      if (!token) {
        throw new Error('No authentication token found');
      }

      const validData = excelData.filter(row =>
        row.name && row.email && row.category && (row.website || row.has_website === false)
      );

      if (validData.length === 0) {
        throw new Error('No valid supplier data found. All suppliers must have name, email, and category. Website is optional if "Has Website" is unchecked.');
      }

      const results = [];
      for (const supplierData of validData) {
        try {
          const submitData = {
            ...supplierData,
            tags: supplierData.tags ? supplierData.tags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag) : [],
            online_sales: supplierData.online_sales === 'true' || supplierData.online_sales === true,
            online_marketing: supplierData.online_marketing === 'true' || supplierData.online_marketing === true,
            trustpilot_score: parseFloat(supplierData.trustpilot_score) || 0,
            google_maps_score: parseFloat(supplierData.google_maps_score) || 0,
            mebsis_score: parseFloat(supplierData.mebsis_score) || 0,
            email_score: parseFloat(supplierData.email_score) || 0
          };

          const response = await fetch(API_ENDPOINTS.SUPPLIERS_V2, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(submitData)
          });

          if (response.ok) {
            const result = await response.json();
            results.push({ success: true, data: result.data });
          } else {
            const errorData = await response.json();
            let errorMessage = `Failed to create ${supplierData.name}`;

            // Handle domain conflict specifically
            if (response.status === 409 && errorData.existing_supplier) {
              const existing = errorData.existing_supplier;
              errorMessage = `Domain "${existing.website}" already exists (${existing.name})`;
            } else if (errorData.error) {
              errorMessage = errorData.error;
            }

            results.push({ success: false, error: errorMessage });
          }
        } catch (error) {
          results.push({ success: false, error: `Error creating ${supplierData.name}` });
        }
      }

      const successCount = results.filter(r => r.success).length;
      if (successCount > 0) {
        // Call success callback
        if (onSuccess) {
          onSuccess();
        }
        onClose();
        alert(`Successfully created ${successCount} suppliers`);
      } else {
        throw new Error('Failed to create any suppliers');
      }
    } catch (error) {
      console.error('Error creating suppliers:', error);
      setError(error instanceof Error ? error.message : 'Failed to create suppliers');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Excel preview functions
  const handleEditRow = (index: number) => {
    setEditingRow(index);
    setEditedData({ ...excelData[index] });
  };

  const handleSaveRow = () => {
    if (editingRow !== null) {
      const updatedData = [...excelData];
      updatedData[editingRow] = { ...editedData };
      setExcelData(updatedData);
      setEditingRow(null);
      setEditedData({});
    }
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditedData({});
  };

  const handleDeleteRow = (index: number) => {
    const updatedData = excelData.filter((_, i) => i !== index);
    setExcelData(updatedData);
    setSelectedRows(new Set());
  };

  const handleSelectRow = (index: number) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedRows.size === excelData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(excelData.map((_, index) => index)));
    }
  };

  const handleDeleteSelected = () => {
    const updatedData = excelData.filter((_, index) => !selectedRows.has(index));
    setExcelData(updatedData);
    setSelectedRows(new Set());
  };

  const handleEditedDataChange = (field: string, value: any) => {
    setEditedData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  // Supplier name suggestion functions
  const fetchNameSuggestions = async (name: string) => {
    console.log('🔍 fetchNameSuggestions called with:', name);

    if (!name || name.length < 2) {
      console.log('❌ Name too short or empty');
      setWebsiteSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      console.log('🔑 Token found:', !!token);

      if (!token) {
        console.log('❌ No token found');
        return;
      }

      const url = `${API_ENDPOINTS.SUPPLIER_NAME_SUGGEST}?name=${encodeURIComponent(name)}&limit=5`;
      console.log('🌐 Fetching URL:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('✅ API Response:', result);

        if (result.success) {
          console.log('🎯 Suggestions found:', result.data.length);
          setWebsiteSuggestions(result.data);
          setShowSuggestions(result.data.length > 0);
        } else {
          console.log('❌ API returned success: false');
        }
      } else {
        const errorText = await response.text();
        console.log('❌ API Error:', response.status, errorText);
      }
    } catch (error) {
      console.error('💥 Error fetching name suggestions:', error);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    console.log('📝 Name input changed:', value);

    setFormData(prev => ({ ...prev, name: value }));

    // Clear existing timeout
    if (suggestionTimeout) {
      console.log('⏰ Clearing existing timeout');
      clearTimeout(suggestionTimeout);
    }

    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      console.log('⏰ Timeout triggered, fetching suggestions for:', value);
      fetchNameSuggestions(value);
    }, 300);

    setSuggestionTimeout(timeout);
    console.log('⏰ New timeout set');
  };

  const handleSuggestionSelect = (suggestion: any) => {
    setFormData({
      supplier_id: suggestion.supplier_id,  // Store supplier_id for existing supplier selection
      name: suggestion.name,
      emails: suggestion.emails || [suggestion.email || ''],
      phone: suggestion.phone,
      website: suggestion.website,
      region: suggestion.region,
      categories: suggestion.categories || [suggestion.category || ''],
      contact_person: suggestion.contact_person,
      notes: suggestion.notes,
      address: suggestion.address,
      commercial_name: suggestion.commercial_name,
      online_sales: suggestion.online_sales,
      trustpilot_score: suggestion.trustpilot_score,
      google_maps_score: suggestion.google_maps_score,
      mebsis_score: suggestion.mebsis_score,
      tags: Array.isArray(suggestion.tags) ? suggestion.tags.join(', ') : suggestion.tags,
      mebsis_registration: suggestion.mebsis_registration,
      has_website: !!suggestion.website
    });

    setShowSuggestions(false);
    setWebsiteSuggestions([]);
  };

  const hideSuggestions = () => {
    // Delay hiding to allow click events to fire
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay-v2">
      <div className="modal-content-v2">
        <div className="modal-header-v2">
          <h2 className="modal-title-v2">Add New Supplier</h2>
          <button className="modal-close-v2" onClick={handleClose}>×</button>
        </div>

        {/* Tab Navigation */}
        <div className="modal-tabs-v2">
          <button
            className={`tab-button-v2 ${activeTab === 'single' ? 'active' : ''}`}
            onClick={() => setActiveTab('single')}
          >
            Single Entry
          </button>
          <button
            className={`tab-button-v2 ${activeTab === 'excel' ? 'active' : ''}`}
            onClick={() => setActiveTab('excel')}
          >
            Excel Import
          </button>
        </div>

        {activeTab === 'single' && (
          <form onSubmit={handleSubmit} className="modal-form-v2 supplier-form-v2">
            <div className="form-group-v2">
              <label htmlFor="name">Supplier Name *</label>
              <div className="name-suggestion-container">
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleNameChange}
                  onBlur={hideSuggestions}
                  required
                  autoComplete="off"
                />
                {showSuggestions && websiteSuggestions.length > 0 && (
                  <div className="name-suggestions">
                    {websiteSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="suggestion-item"
                        onClick={() => handleSuggestionSelect(suggestion)}
                      >
                        <div className="suggestion-name">{suggestion.name}</div>
                        <div className="suggestion-details">
                          {suggestion.email} • {suggestion.category} • {suggestion.region}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row-v2">
              <div className="form-group-v2">
                <label>Emails *</label>
                {formData.emails.map((email, index) => (
                  <div key={index} className="email-input-group">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => updateEmail(index, e.target.value)}
                      placeholder="Enter email address"
                      required
                    />
                    {formData.emails.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEmail(index)}
                        className="remove-email-btn"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addEmail}
                  className={`add-email-btn ${isBasicUser ? 'blue-variant' : ''}`}
                >
                  + Add Email
                </button>
              </div>
              <div className="form-group-v2">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row-v2">
              <div className="form-group-v2">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  disabled={!formData.has_website}
                  placeholder={formData.has_website ? "e.g., dk.com or acme.com" : "No website"}
                />
                <div className="checkbox-group-v2">
                  <input
                    type="checkbox"
                    id="has_website"
                    name="has_website"
                    checked={formData.has_website}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="has_website" className="checkbox-label-v2">
                    Has Website
                  </label>
                </div>
              </div>
              <div className="form-group-v2">
                <label htmlFor="region">Region</label>
                <select
                  id="region"
                  name="region"
                  value={formData.region}
                  onChange={handleInputChange}
                >
                  <option value="tr">Turkey</option>
                  <option value="en">Europe/International</option>
                  <option value="de">Germany</option>

                </select>
              </div>
            </div>

            <div className="form-row-v2">
              <div className="form-group-v2">
                <label>Categories *</label>
                <CategorySelector
                  value={formData.categories}
                  onChange={(categoryIds) => setFormData({ ...formData, categories: categoryIds })}
                  language="tr"
                  multiple={true}
                  placeholder="Select categories..."
                />
              </div>
              <div className="form-group-v2">
                <label htmlFor="contact_person">Contact Person</label>
                <input
                  type="text"
                  id="contact_person"
                  name="contact_person"
                  value={formData.contact_person}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-group-v2">
              <label htmlFor="address">Address</label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows={3}
              />
            </div>

            <div className="form-row-v2">
              <div className="form-group-v2">
                <label htmlFor="commercial_name">Commercial Name</label>
                <input
                  type="text"
                  id="commercial_name"
                  name="commercial_name"
                  value={formData.commercial_name}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group-v2">
                <label htmlFor="mebsis_registration">Mebsis Registration</label>
                <select
                  id="mebsis_registration"
                  name="mebsis_registration"
                  value={formData.mebsis_registration}
                  onChange={handleInputChange}
                >
                  <option value="Not Registered">Not Registered</option>
                  <option value="Active">Active</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
            </div>

            {!isBasicUser && (
              <div className="form-row-v2">
                <div className="form-group-v2 checkbox-group-v2">
                  <label>
                    <input
                      type="checkbox"
                      name="online_sales"
                      checked={formData.online_sales}
                      onChange={handleInputChange}
                    />
                    Online Sales
                  </label>
                </div>
              </div>
            )}

            {!isBasicUser && (
              <>
                <div className="form-row-v2">
                  <div className="form-group-v2">
                    <label htmlFor="trustpilot_score">Trustpilot Score</label>
                    <input
                      type="number"
                      id="trustpilot_score"
                      name="trustpilot_score"
                      value={formData.trustpilot_score}
                      onChange={handleInputChange}
                      min="0"
                      max="5"
                      step="0.1"
                    />
                  </div>
                  <div className="form-group-v2">
                    <label htmlFor="google_maps_score">Google Maps Score</label>
                    <input
                      type="number"
                      id="google_maps_score"
                      name="google_maps_score"
                      value={formData.google_maps_score}
                      onChange={handleInputChange}
                      min="0"
                      max="5"
                      step="0.1"
                    />
                  </div>
                </div>

                <div className="form-row-v2">
                  <div className="form-group-v2">
                    <label htmlFor="mebsis_score">Mebsis Score</label>
                    <input
                      type="number"
                      id="mebsis_score"
                      name="mebsis_score"
                      value={formData.mebsis_score}
                      onChange={handleInputChange}
                      min="0"
                      max="5"
                      step="0.1"
                    />
                  </div>
                </div>

                <div className="form-group-v2">
                  <label htmlFor="tags">Tags (comma-separated)</label>
                  <input
                    type="text"
                    id="tags"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    placeholder="e.g., industrial, services, manufacturing"
                  />
                </div>
              </>
            )}

            {/* Company Specific Codes Section */}
            <div className="form-group-v2">
              <label>
                {companyId ? `${companyName} Code (Optional - default: "1")` : 'Company Specific Codes (Optional - default: "1")'}
              </label>
              {!companyId && (
                <CompanySelector
                  value={selectedCompanies}
                  onChange={setSelectedCompanies}
                  placeholder="Select companies to add codes..."
                  multiple={true}
                  usePortal={false}
                />
              )}
              {companyId && (
                <div style={{
                  padding: '8px 12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  fontSize: '13px',
                  color: '#6c757d',
                  marginTop: '0.5rem'
                }}>
                  Company: <strong>{companyName}</strong> (automatically selected)
                </div>
              )}
            </div>

            {selectedCompanies.length > 0 && (
              <div className="form-group-v2" style={{ marginTop: '0.5rem' }}>
                <label>Company Codes</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {selectedCompanies.map((company) => (
                    <div key={company.company_id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <label style={{ minWidth: '200px', fontSize: '13px' }}>
                        {company.company_name}:
                      </label>
                      <input
                        type="text"
                        value={companyCodes[company.company_id] || ''}
                        onChange={(e) => setCompanyCodes({
                          ...companyCodes,
                          [company.company_id]: e.target.value
                        })}
                        placeholder="Enter code (default: 1)..."
                        style={{
                          flex: 1,
                          padding: '8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '13px'
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="form-group-v2">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                placeholder="Additional notes about the supplier"
              />
            </div>

            {error && (
              <div className="error-message-v2">
                {error}
              </div>
            )}

            <div className="modal-actions-v2">
              <button type="button" onClick={handleClose} className="cancel-button-v2">
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="submit-button-v2"
              >
                {isSubmitting ? 'Creating...' : 'Create Supplier'}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'excel' && (
          <div className="excel-section-v2">
            <div className="excel-actions-v2">
              <button
                type="button"
                onClick={downloadTemplate}
                className="template-button-v2"
              >
                Download Template
              </button>

              <div className="file-input-wrapper-v2">
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleExcelUpload}
                  className="file-input-v2"
                  id="excel-upload"
                />
                <label htmlFor="excel-upload" className="upload-button-v2">
                  Upload Excel File
                </label>
              </div>
            </div>

            {showPreview && excelData.length > 0 && (
              <div className="preview-section-v2">
                <h3>Preview Data ({excelData.length} suppliers)</h3>

                <div className="preview-table-v2">
                  <table>
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={selectedRows.size === excelData.length && excelData.length > 0}
                            onChange={handleSelectAll}
                            className="select-all-checkbox-v2"
                          />
                        </th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Category</th>
                        <th>Phone</th>
                        <th>WhatsApp</th>
                        <th>Website</th>
                        <th>Region</th>
                      </tr>
                    </thead>
                    <tbody>
                      {excelData.map((row, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedRows.has(index)}
                              onChange={() => handleSelectRow(index)}
                              className="row-checkbox-v2"
                            />
                          </td>
                          <td>{row.name}</td>
                          <td>{row.email}</td>
                          <td>{row.category}</td>
                          <td>{row.phone}</td>
                          <td>{row.whatsapp}</td>
                          <td>{row.website}</td>
                          <td>{row.region}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="preview-actions-v2">
                  <div className="edit-controls-v2">
                    {editingRow === null ? (
                      <button
                        type="button"
                        onClick={() => handleEditRow(0)}
                        className="edit-selected-button-v2"
                      >
                        Edit First Row
                      </button>
                    ) : (
                      <div className="edit-save-cancel-v2">
                        <button
                          type="button"
                          onClick={handleSaveRow}
                          className="save-edit-button-v2"
                        >
                          Save Changes
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          className="cancel-edit-button-v2"
                        >
                          Cancel Edit
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="delete-controls-v2">
                    <button
                      type="button"
                      onClick={handleDeleteSelected}
                      disabled={selectedRows.size === 0}
                      className="delete-selected-button-v2"
                    >
                      Delete Selected ({selectedRows.size})
                    </button>
                  </div>
                </div>

                {editingRow !== null && (
                  <div className="edit-actions-v2">
                    <h4>Edit Row {editingRow + 1}:</h4>
                    <div className="edit-form-v2">
                      <div className="edit-row-v2">
                        <input
                          type="text"
                          value={editedData.name || ''}
                          onChange={(e) => handleEditedDataChange('name', e.target.value)}
                          placeholder="Name"
                          className="edit-input-v2"
                        />
                        <input
                          type="email"
                          value={editedData.email || ''}
                          onChange={(e) => handleEditedDataChange('email', e.target.value)}
                          placeholder="Email"
                          className="edit-input-v2"
                        />
                        <input
                          type="text"
                          value={editedData.category || ''}
                          onChange={(e) => handleEditedDataChange('category', e.target.value)}
                          placeholder="Category"
                          className="edit-input-v2"
                        />
                      </div>
                      <div className="edit-row-v2">
                        <input
                          type="text"
                          value={editedData.phone || ''}
                          onChange={(e) => handleEditedDataChange('phone', e.target.value)}
                          placeholder="Phone"
                          className="edit-input-v2"
                        />
                        <input
                          type="text"
                          value={editedData.whatsapp || ''}
                          onChange={(e) => handleEditedDataChange('whatsapp', e.target.value)}
                          placeholder="WhatsApp"
                          className="edit-input-v2"
                        />
                        <input
                          type="url"
                          value={editedData.website || ''}
                          onChange={(e) => handleEditedDataChange('website', e.target.value)}
                          placeholder="Website"
                          className="edit-input-v2"
                        />
                      </div>
                      <div className="edit-row-v2">
                        <select
                          value={editedData.region || 'Turkey'}
                          onChange={(e) => handleEditedDataChange('region', e.target.value)}
                          className="edit-input-v2"
                        >
                          <option value="tr">Turkey</option>
                          <option value="en">Europe/International</option>
                          <option value="de">Germany</option>

                        </select>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bulk-actions-v2">
                  <button
                    type="button"
                    onClick={handleBulkCreate}
                    disabled={isSubmitting}
                    className="submit-button-v2"
                  >
                    {isSubmitting ? 'Creating Suppliers...' : `Create ${excelData.length} Suppliers`}
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="error-message-v2">
                {error}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddSupplierModal;
